import type { ContentType, Movie } from "@/types/movie";
import { movieItems } from "@/mocks/movies";
import { tvItems } from "@/mocks/tvShows";

export const allMovies: Movie[] = [...movieItems, ...tvItems] as Movie[];

export const typeLabel = (type: ContentType): string =>
  type === "movie" ? "电影" : "电视剧";

export const getMovieById = (id?: string): Movie | undefined =>
  allMovies.find((item) => item.id === id);

export const getMoviesByType = (type: ContentType): Movie[] =>
  allMovies.filter((item) => item.type === type);

export const getFeatured = (): Movie[] =>
  allMovies.filter((item) => item.featured);

export const sortByUpdated = (list: Movie[]): Movie[] =>
  [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

export const sortByPopular = (list: Movie[]): Movie[] =>
  [...list].sort((a, b) => b.views - a.views);

export const getAllGenres = (type?: ContentType): string[] => {
  const source = type ? getMoviesByType(type) : allMovies;
  return Array.from(new Set(source.flatMap((item) => item.genres)));
};

export const allRegions: string[] = Array.from(
  new Set(allMovies.map((item) => item.region))
);

export interface YearRange {
  key: string;
  label: string;
  min: number;
  max: number;
}

export const yearRanges: YearRange[] = [
  { key: "all", label: "全部年份", min: 0, max: 9999 },
  { key: "recent5", label: "近 5 年", min: 2021, max: 2026 },
  { key: "2023-2024", label: "2023 - 2024", min: 2023, max: 2024 },
  { key: "2021-2022", label: "2021 - 2022", min: 2021, max: 2022 },
  { key: "2019-2020", label: "2019 - 2020", min: 2019, max: 2020 },
];

export const matchYearRange = (year: number, key: string): boolean => {
  const range = yearRanges.find((item) => item.key === key);
  if (!range || key === "all") return true;
  return year >= range.min && year <= range.max;
};

export const searchMovies = (query: string): Movie[] => {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return [];
  return allMovies.filter((item) => {
    const haystack = [
      item.title,
      item.director,
      item.region,
      item.summary,
      item.synopsis,
      ...item.cast,
      ...item.genres,
      typeLabel(item.type),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(keyword);
  });
};

export const getRelated = (movie: Movie, limit = 4): Movie[] => {
  const scored = allMovies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.type === movie.type) score += 2;
      item.genres.forEach((genre) => {
        if (movie.genres.includes(genre)) score += 3;
      });
      if (item.region === movie.region) score += 1;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views);

  return scored.slice(0, limit).map((entry) => entry.item);
};

export const paginate = <T,>(list: T[], page: number, pageSize: number): T[] => {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
};